import { Capacitor } from '@capacitor/core';

/**
 * Platform Detection Service
 * Determines which payment system to use based on the running platform
 * 
 * - iOS → Apple In-App Purchases (required by Apple)
 * - Android → Stripe (existing implementation)
 * - Web → Stripe (existing implementation)
 * 
 * PRICING:
 * iOS prices are slightly higher (Apple's 30% commission)
 * Android/Web prices remain the same via Stripe
 */

// ============================================
// CREDIT PACKAGES (all platforms, fixed packages only)
// ============================================

// Android/Web credit packages (Stripe) - prețuri mai mici
export const STRIPE_CREDIT_PACKAGES = [
  { credits: 10, price: 1.00, priceLabel: '€1.00' },
  { credits: 50, price: 5.00, priceLabel: '€5.00' },
  { credits: 100, price: 9.00, priceLabel: '€9.00' },
  { credits: 200, price: 16.00, priceLabel: '€16.00' },
] as const;

// iOS credit packages (Apple IAP) - prețuri puțin mai mari
export const APPLE_CREDIT_PRODUCTS = [
  { productId: 'fiifit_credits_10_ro', credits: 10, price: 1.49, priceLabel: '€1.49', name: '10 Extra Credits' },
  { productId: 'fiifit_credits_50_ro', credits: 50, price: 5.99, priceLabel: '€5.99', name: '50 Extra Credits' },
  { productId: 'fiifit_credits_100_ro', credits: 100, price: 9.99, priceLabel: '€9.99', name: '100 Extra Credits' },
  { productId: 'fiifit_credits_200_ro', credits: 200, price: 16.99, priceLabel: '€16.99', name: '200 Extra Credits' },
] as const;

// ============================================
// SUBSCRIPTION PRODUCTS
// ============================================

// Apple subscription products (must match App Store Connect)
// IMPORTANT: Price values below trebuie să corespundă EXACT cu prețurile
// configurate în App Store Connect pentru fiecare productId.
// Dacă modifici aici, trebuie să ajustezi și price tier-ul în App Store Connect.
// Le aliniem cu prețurile din admin/web pentru consistență cross-platform.
export const APPLE_SUBSCRIPTION_PRODUCTS = [
  { productId: 'fiifit_basic_monthly_ro', tier: 'BASIC', name: 'Plan Basic', monthlyCredits: 50, price: 5.99, priceLabel: '€5.99/lună' },
  { productId: 'fiifit_premium_monthly_ro', tier: 'PREMIUM', name: 'Plan Premium', monthlyCredits: 100, price: 9.99, priceLabel: '€9.99/lună' },
  { productId: 'fiifit_unlimited_monthly_ro', tier: 'UNLIMITED', name: 'Plan Gold', monthlyCredits: 250, price: 19.99, priceLabel: '€19.99/lună' },
] as const;

// Android/Web subscription prices (Stripe) — aliniate cu admin
export const STRIPE_SUBSCRIPTION_PRICES = {
  BASIC: { price: 5.99, priceLabel: '€5.99/lună' },
  PREMIUM: { price: 9.99, priceLabel: '€9.99/lună' },
  UNLIMITED: { price: 19.99, priceLabel: '€19.99/lună' },
} as const;


// ============================================
// PLATFORM DETECTION
// ============================================

export function getPlatform(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}

export function isIOS(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

export function isAndroid(): boolean {
  return Capacitor.getPlatform() === 'android';
}

export function isWeb(): boolean {
  return Capacitor.getPlatform() === 'web';
}

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function shouldUseAppleIAP(): boolean {
  return isIOS();
}

export function shouldUseStripe(): boolean {
  return !isIOS();
}

// ============================================
// PRODUCT LOOKUP HELPERS
// ============================================

export function getAppleProductIdForTier(tier: string): string | null {
  const product = APPLE_SUBSCRIPTION_PRODUCTS.find(p => p.tier === tier);
  return product?.productId || null;
}

export function getAppleProductIdForCredits(credits: number): string | null {
  const product = APPLE_CREDIT_PRODUCTS.find(p => p.credits === credits);
  return product?.productId || null;
}

/**
 * Get credit package info for display (platform-aware)
 */
export function getCreditPackages() {
  if (isIOS()) {
    return APPLE_CREDIT_PRODUCTS.map(p => ({
      credits: p.credits,
      price: p.price,
      priceLabel: p.priceLabel,
    }));
  }
  return STRIPE_CREDIT_PACKAGES.map(p => ({
    credits: p.credits,
    price: p.price,
    priceLabel: p.priceLabel,
  }));
}

export function getPaymentMethodLabel(): string {
  if (isIOS()) return 'Apple';
  return 'Stripe';
}
